// Path: ./src/utils/sessionExpiry.ts
import { clearAuthToken } from "./authTokenStorage";

/**
 * Shared "your login expired" state.
 *
 * Any request that gets a 401 awaits `waitForReauth()`. The first one clears
 * the dead token and opens the sign-in dialog (SessionExpiredDialog); requests
 * that fail while it is open join the same wait. The dialog calls
 * `finishReauth(newToken)` after a successful sign-in so every waiting request
 * can retry, or `finishReauth(null)` when the user signs out instead.
 *
 * Nothing here reloads or redirects, so unsaved work on screen survives.
 */
type Listener = () => void;

let pending: Promise<string | null> | null = null;
let resolvePending: ((token: string | null) => void) | null = null;
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((listener) => listener());

export const waitForReauth = (): Promise<string | null> => {
  if (!pending) {
    clearAuthToken();
    pending = new Promise((resolve) => {
      resolvePending = resolve;
    });
    notify();
  }
  return pending;
};

export const finishReauth = (token: string | null): void => {
  const resolve = resolvePending;
  pending = null;
  resolvePending = null;
  notify();
  resolve?.(token);
};

export const isReauthPending = (): boolean => pending !== null;

export const subscribeToReauth = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
